/**
 * Analytics Dashboard Configuration
 * 
 * To ADD a new analytics tab:
 * 1. Add entry to ANALYTICS_TABS array below
 * 2. Create component in /components/analytics/<TabId>Tab.js
 * 3. Backend must return data for the tab's dataKey
 * 
 * To REMOVE an analytics tab:
 * 1. Remove entry from ANALYTICS_TABS array
 * 2. Optionally delete the component file
 */

import {
  DollarSign,
  Users,
  Package,
  TrendingUp,
  Calendar,
  BarChart3,
} from "lucide-react";

export const ANALYTICS_TABS = [
  {
    id: "profitability",
    label: "Profitability",
    icon: DollarSign,
    dataKey: "job_profitability",
    exportable: true,
    exportFileName: "job-profitability",
    exportHeaders: ["Job Name", "Client", "Status", "Estimate", "Actual", "Profit/Loss", "Margin %"],
    exportFieldMapping: {
      "Job Name": "job_name",
      "Client": "client_name", 
      "Status": "status",
      "Estimate": "total_estimate",
      "Actual": "total_actual",
      "Profit/Loss": "profit_loss",
      "Margin %": "margin_pct",
    },
    dateField: "start_date",
  },
  {
    id: "labor",
    label: "Labor",
    icon: Users,
    dataKey: "labor_productivity",
    exportable: true,
    exportFileName: "labor-productivity",
    exportHeaders: ["Job Name", "Status", "Hours", "Estimate", "Actual", "Budget Used %", "$/Hour"],
    exportFieldMapping: {
      "Job Name": "job_name",
      "Status": "status",
      "Hours": "total_hours",
      "Estimate": "labor_estimate",
      "Actual": "labor_actual",
      "Budget Used %": "budget_used_pct",
      "$/Hour": "cost_per_hour",
    },
    dateField: null,
  },
  {
    id: "materials",
    label: "Materials",
    icon: Package,
    dataKey: "material_usage",
    exportable: true,
    exportFileName: "material-usage",
    exportHeaders: ["Material", "Category", "Quantity", "Total Cost"],
    exportFieldMapping: {
      "Material": "name",
      "Category": "category",
      "Quantity": "quantity",
      "Total Cost": "cost",
    },
    dateField: null,
    nestedDataKey: "by_material",
  },
  {
    id: "revenue",
    label: "Revenue",
    icon: TrendingUp,
    dataKey: "revenue_cashflow",
    exportable: true,
    exportFileName: "revenue-cashflow",
    exportHeaders: ["Month", "Billings Sent", "Billings Received", "Contract Value"],
    exportFieldMapping: {
      "Month": "month",
      "Billings Sent": "billings_sent",
      "Billings Received": "billings_received",
      "Contract Value": "contract_value",
    },
    dateField: null,
    nestedDataKey: "monthly_trend",
  },
  {
    id: "employees",
    label: "Employees",
    icon: Users,
    dataKey: "employee_performance",
    exportable: true,
    exportFileName: "employee-performance",
    exportHeaders: ["Name", "Position", "Hours", "Earnings", "Jobs", "Days", "Avg Hours/Day"],
    exportFieldMapping: {
      "Name": "employee_name",
      "Position": "position",
      "Hours": "total_hours",
      "Earnings": "total_earnings",
      "Jobs": "jobs_worked",
      "Days": "days_worked",
      "Avg Hours/Day": "avg_hours_per_day",
    },
    dateField: null,
  },
  {
    id: "seasonal",
    label: "Seasonal",
    icon: Calendar,
    dataKey: "seasonal_trends",
    exportable: true,
    exportFileName: "seasonal-trends",
    exportHeaders: ["Month", "Jobs Started", "Jobs Completed", "Hours", "Revenue"],
    exportFieldMapping: {
      "Month": "month",
      "Jobs Started": "jobs_started",
      "Jobs Completed": "jobs_completed",
      "Hours": "total_hours",
      "Revenue": "revenue",
    },
    dateField: null,
    nestedDataKey: "monthly",
  },
];

// Summary cards configuration
export const SUMMARY_CARDS = [
  { key: "total_jobs", label: "Total Jobs", icon: "BarChart3" },
  { key: "active_jobs", label: "Active Jobs", icon: "TrendingUp" },
  { key: "total_revenue", label: "Total Revenue", format: "currency", icon: "DollarSign" },
  { key: "total_profit", label: "Total Profit", format: "currency", colorByValue: true, icon: "TrendingUp" },
  { key: "avg_margin_pct", label: "Avg Margin", format: "percent", icon: "BarChart3" },
  { key: "total_employees", label: "Employees", icon: "Users" },
];

// Date range filter options
export const DATE_RANGE_OPTIONS = [
  { label: "Last 30 Days", value: "30d" },
  { label: "Last 90 Days", value: "90d" },
  { label: "Last 365 Days", value: "365d" },
  { label: "Year to Date", value: "ytd" },
  { label: "All Time", value: "all" },
];

// Status filter options  
export const STATUS_FILTER_OPTIONS = [
  { label: "Active & Complete", value: "active_complete", statuses: ["active", "complete"] },
  { label: "All Statuses", value: "all", statuses: ["bidding", "precon", "active", "complete", "archive", "not_bid", "bid_complete", "not_won"] },
  { label: "Bidding Only", value: "bidding", statuses: ["bidding"] },
  { label: "Active Only", value: "active", statuses: ["active"] },
  { label: "Complete Only", value: "complete", statuses: ["complete"] },
  { label: "Include Precon", value: "with_precon", statuses: ["precon", "active", "complete"] },
  { label: "Include Bidding", value: "with_bidding", statuses: ["bidding", "precon", "active", "complete"] },
  { label: "Archived Only", value: "archived", statuses: ["archive", "not_bid", "bid_complete", "not_won"] },
];

// Get tab config by ID
export const getTabConfig = (tabId) => ANALYTICS_TABS.find(t => t.id === tabId);

// Get all tab IDs
export const getTabIds = () => ANALYTICS_TABS.map(t => t.id);

// Check if tab exists
export const tabExists = (tabId) => ANALYTICS_TABS.some(t => t.id === tabId);
